import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { VendorPerformanceRow } from '@/hooks/useVendorPerformance';
import { cn } from '@/lib/utils';

interface VendorPerformanceTableProps {
  data: VendorPerformanceRow[] | undefined;
  isLoading: boolean;
}

type SortField = 'vendorName' | 'bidsSubmitted' | 'avgResponseTimeHours' | 'winRate' | 'jobsCompleted' | 'totalValue' | 'avgJobValue';
type SortDirection = 'asc' | 'desc';

const ROWS_PER_PAGE = 20;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTime(hours: number): string {
  if (hours === 0) return 'N/A';
  if (hours < 48) {
    return `${Math.round(hours)}h`;
  }
  return `${(hours / 24).toFixed(1)}d`;
}

function getCertBadgeColor(cert: string): string {
  const certLower = cert.toLowerCase();
  if (certLower.includes('mbe') || certLower.includes('minority')) return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
  if (certLower.includes('wbe') || certLower.includes('women')) return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
  if (certLower.includes('vbe') || certLower.includes('veteran')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  if (certLower.includes('sdvosb') || certLower.includes('service')) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  if (certLower.includes('hub') || certLower.includes('8(a)')) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
}

function SortableHeader({
  label,
  field,
  currentSort,
  currentDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentDirection: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  const isActive = currentSort === field;
  
  return (
    <button
      onClick={() => onSort(field)}
      className={cn(
        'flex items-center gap-1 hover:text-foreground transition-colors w-full',
        align === 'right' && 'justify-end'
      )}
    >
      {label}
      {isActive ? (
        currentDirection === 'asc' ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )
      ) : (
        <ChevronDown className="h-4 w-4 opacity-30" />
      )}
    </button>
  );
}

export function VendorPerformanceTable({ data, isLoading }: VendorPerformanceTableProps) {
  const [sortField, setSortField] = useState<SortField>('totalValue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Performance</CardTitle>
          <CardDescription>Detailed vendor metrics for the selected period</CardDescription>
        </CardHeader>
        <CardContent className="h-[400px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Performance</CardTitle>
          <CardDescription>Detailed vendor metrics for the selected period</CardDescription>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <p className="text-muted-foreground">
            No vendor activity found for this period. Try expanding your date range or adjusting filters.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Sort data
  const sortedData = [...data].sort((a, b) => {
    let aVal: number | string = a[sortField];
    let bVal: number | string = b[sortField];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
  });

  // Paginate
  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE);
  const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
  const paginatedData = sortedData.slice(startIdx, startIdx + ROWS_PER_PAGE);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendor Performance</CardTitle>
        <CardDescription>
          Detailed vendor metrics for the selected period • {data.length} vendors
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">
                  <SortableHeader
                    label="Vendor Name"
                    field="vendorName"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Bids"
                    field="bidsSubmitted"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Avg Response"
                    field="avgResponseTimeHours"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Win Rate"
                    field="winRate"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Jobs"
                    field="jobsCompleted"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Total Value"
                    field="totalValue"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader
                    label="Avg Job Value"
                    field="avgJobValue"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((vendor) => (
                <TableRow key={vendor.vendorId} className="hover:bg-muted/50">
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{vendor.vendorName}</span>
                      {vendor.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {vendor.certifications.slice(0, 3).map((cert, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className={cn('text-xs px-1.5 py-0', getCertBadgeColor(cert))}
                            >
                              {cert.length > 6 ? cert.substring(0, 6) : cert}
                            </Badge>
                          ))}
                          {vendor.certifications.length > 3 && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              +{vendor.certifications.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{vendor.bidsSubmitted}</TableCell>
                  <TableCell className="text-right">{formatTime(vendor.avgResponseTimeHours)}</TableCell>
                  <TableCell className="text-right">{vendor.winRate.toFixed(0)}%</TableCell>
                  <TableCell className="text-right">{vendor.jobsCompleted}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(vendor.totalValue)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(vendor.avgJobValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {startIdx + 1}-{Math.min(startIdx + ROWS_PER_PAGE, data.length)} of {data.length} vendors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
